// Scripts editor: one scene row of the script table (memoized) and the colour swatch picker.
// HD-7: on src/ui and role tokens (the .ui-sheet block in shell/page.css). Handlers and focus flow unchanged from the old
// pages/Scripts.jsx; the scene-* classes left on the elements are hooks the editor's logic queries, not styling.
import React from 'react';
import { ChevronDown, Link2, Plus, X } from 'lucide-react';
import Button from '../../ui/Button';
import Icon from '../../ui/Icon';
import Input from '../../ui/Input';
import {
  ROW_COLORS,
  tintOf,
  sentenceLabel,
  displayCharacters,
  matchCharacterNames,
  registryHasName,
  speakerTwoAbove,
  LINK_COLS,
  extractCellUrl,
  autoResize,
} from './scriptHelpers';

// ── SceneRow (memoized) ──────────────────────────────────────────
// Per-row JSX extracted out so React.memo skips reconciliation for the rows whose
// data didn't change on a given keystroke — at 500 rows that drops the per-keystroke
// render cost from O(N) to O(1). Handlers are stable wrappers from the parent
// (`handlers` prop) that delegate through refs to the current functions; per-row
// state (selection, hover, autocomplete-on-this-row, etc.) is derived in the parent
// and passed as primitives or scoped objects, so memo's shallow compare only treats
// rows whose own state changed as different.
const SceneRow = React.memo(function SceneRow({
  idx, row, cols, format, totalRows,
  isReadOnly, editorFontSize, showQualNotesCol, canEditQualNotes,
  registryNames,        // string[] | null — live client-registry names (Skit/Mock only); null = no picker
  canEditRegistry,      // bool — shows the one-click "Add to list" on a not-in-list name
  isSelected, isCut, isHovered,
  dropMarker,           // 'above' | 'end' | null
  autocomplete,         // object | null (only when on this row)
  focusedColKey,        // c.key | null — drives the spec 1.2 active-cell border
  editingColKey,        // c.key | null — when set, that cell renders <textarea>; others render idle <div>
  editingMode,          // 'cursor-end' | 'select-all' | 'cursor-start' — read once on textarea mount
  selectedColRange,     // '' or 'c1-c2' (inclusive colIdx range) — spec 1.5 selection membership for THIS row
  multiSelectedColIdxs, // '' or comma-joined colIdxs (e.g. '0,2,5') — non-contiguous cells in THIS row
  moveTargetColRange,   // '' or 'c1-c2' — spec 4.1/4.2 move-drag target columns on THIS row
  autofillHandleColIdx, // colIdx | null — spec 5.2 anchor cell for the autofill handle
  autofillPreviewRange, // '' or 'c1-c2' — spec 5.2 fill-preview cells on THIS row
  colorPickerOpen,      // boolean
  colorPickerCellKey,   // c.key | null
  linkPopoverColKey,    // c.key | null
  linkDraft,            // { text, url } | null (only when popover on this row)
  setHoveredRow, setColorPickerRow, setColorPickerCell,
  setLinkPopover, setLinkDraft, setAutocomplete,
  setSelectedRows, setSelectionAnchor, setSelectAllActive, setFocusedCell,
  handlers,
  draftBodyRef, selectedRowsSizeRef, selectAllActiveRef, lastFocusBodyRef,
}) {
  // D-129 (2026-09-18): EPISODE BREAK / EPISODE n marker rows are not emitted as grid rows (the episode band
  // is the break), and collapsed rows are not emitted either — so "the next row" may not be in the DOM.
  // focusVisible walks past missing rows in the given direction; false when nothing visible is left.
  const focusVisible = (fromIdx, colKey, dir = 1) => {
    const total = draftBodyRef.current?.scenes?.length || 0;
    for (let i = fromIdx; i >= 0 && i < total; i += dir) {
      const el = document.querySelector(`[data-row="${i}"][data-col="${colKey}"]`);
      if (el) { el.focus(); return true; }
    }
    return false;
  };
  const {
    updateScene, applyMarkdown, addRow, moveRow,
    handleGutterMouseDown, updateRowColor, updateCellColor,
    removeLink, saveLink, pushUndo, saveBody,
  } = handlers;

  // Phase B2: derive which column indices in this row are inside the selection
  // range. '' means not in range; 'c1-c2' means inclusive [c1, c2]. Also folds
  // in the non-contiguous multi-selection's colIdxs for this row.
  const selectedColIdxSet = React.useMemo(() => {
    if (!selectedColRange && !multiSelectedColIdxs) return null;
    const s = new Set();
    if (selectedColRange) {
      const [c1Str, c2Str] = selectedColRange.split('-');
      const c1 = Number(c1Str), c2 = Number(c2Str);
      for (let i = c1; i <= c2; i++) s.add(i);
    }
    if (multiSelectedColIdxs) {
      for (const v of multiSelectedColIdxs.split(',')) s.add(Number(v));
    }
    return s;
  }, [selectedColRange, multiSelectedColIdxs]);
  // Phase C1: same shape for move-drag target columns on this row.
  const moveTargetColIdxSet = React.useMemo(() => {
    if (!moveTargetColRange) return null;
    const [c1Str, c2Str] = moveTargetColRange.split('-');
    const c1 = Number(c1Str), c2 = Number(c2Str);
    const s = new Set();
    for (let i = c1; i <= c2; i++) s.add(i);
    return s;
  }, [moveTargetColRange]);
  // Phase C2: autofill preview tint columns on this row.
  const autofillPreviewColIdxSet = React.useMemo(() => {
    if (!autofillPreviewRange) return null;
    const [c1Str, c2Str] = autofillPreviewRange.split('-');
    const c1 = Number(c1Str), c2 = Number(c2Str);
    const s = new Set();
    for (let i = c1; i <= c2; i++) s.add(i);
    return s;
  }, [autofillPreviewRange]);

  // Phase B1: when the editing cell changes (or its mode changes), grab focus
  // on the just-mounted textarea and apply caret/selection per mode. Runs in
  // a layout effect so focus lands before the browser paints, no flicker.
  React.useLayoutEffect(() => {
    if (!editingColKey) return;
    const el = document.querySelector(`tr[data-row-idx="${idx}"] textarea[data-col="${editingColKey}"]`);
    if (!el) return;
    el.focus();
    autoResize(el);
    const v = el.value || '';
    try {
      if (editingMode === 'select-all')        el.setSelectionRange(0, v.length);
      else if (editingMode === 'cursor-start') el.setSelectionRange(0, 0);
      else                                     el.setSelectionRange(v.length, v.length); // cursor-end
    } catch { /* element may not support selection */ }
  }, [editingColKey, editingMode, idx]);

  return (
    <tr
      data-row-idx={idx}
      className="scene-row ui-sheet__row"
      data-kind={row?.kind === 'direction' ? 'direction' : undefined} /* D-094: a direction row, never a character */
      data-pick={isSelected ? 'true' : undefined}
      data-cut={isCut ? 'true' : undefined}
      data-drop={dropMarker || undefined}
      data-tint={tintOf(row._rowColor)}
      onMouseEnter={() => !isReadOnly && setHoveredRow(idx)}
      onMouseLeave={() => setHoveredRow(null)}
    >
      <td
        className="scene-td scene-num ui-sheet__gutter"
        onMouseDown={e => handleGutterMouseDown(idx, e)}
      >
        <div className="ui-sheet__gutterin">
          <span>{idx + 1}</span>
          {!isReadOnly && isHovered && (
            <>
              <button
                type="button"
                className="ui-sheet__swatchbtn ui-sheet__swatchbtn--row"
                data-tint={tintOf(row._rowColor)}
                aria-label="Row color"
                title="Row color"
                onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setColorPickerRow(colorPickerOpen ? null : idx); setColorPickerCell(null); }}
                onClick={e => e.stopPropagation()}
              />
              {colorPickerOpen && (
                <div className="ui-sheet__pop ui-sheet__pop--gutter" onClick={e => e.stopPropagation()}>
                  <ColorSwatch onSelect={color => updateRowColor(idx, color)} />
                </div>
              )}
            </>
          )}
          {!isHovered && row._rowColor && (
            <span className="ui-sheet__mark" data-tint={tintOf(row._rowColor)} />
          )}
        </div>
      </td>
      {cols.map((c, colIdx) => (
        <td
          key={c.key}
          className="scene-td ui-sheet__cell"
          data-cursor={focusedColKey === c.key ? 'true' : undefined}
          data-range={selectedColIdxSet?.has(colIdx) ? 'true' : undefined}
          data-move={moveTargetColIdxSet?.has(colIdx) ? 'true' : undefined}
          data-fill={autofillPreviewColIdxSet?.has(colIdx) ? 'true' : undefined}
          data-tint={tintOf(row._cellColors?.[c.key])}
          onMouseDown={isReadOnly ? undefined : (e) => {
            // Click landed on the td itself (in its padding area), not on the
            // inner div/textarea. Forward to the inner element so the cell's
            // edge area is clickable too — no more dead "border" around text.
            if (e.target !== e.currentTarget) return;
            const editing = e.currentTarget.querySelector('textarea.scene-input');
            if (editing) { editing.focus(); return; }
            const inner = e.currentTarget.querySelector('.scene-cell-idle');
            if (!inner) return;
            inner.focus();
            handlers.clearTabOrigin();
            if (e.button === 0) handlers.startCellDrag(idx, c.key, e.shiftKey, e.ctrlKey || e.metaKey);
          }}
          onDoubleClick={isReadOnly ? undefined : (e) => {
            // An EMPTY cell collapses the inner idle <div> to ~0 height, so a
            // double-click anywhere in the cell lands on the <td> rather than the
            // div — and the td had no dblclick handler, so edit mode never opened.
            // (Single-click still worked because onMouseDown above forwards focus.)
            // Forward the double-click to enterEdit so empty cells are editable too.
            if (e.target !== e.currentTarget) return; // the inner div handles its own dblclick
            if (e.currentTarget.querySelector('textarea.scene-input')) return; // already editing
            handlers.enterEdit(idx, c.key, 'cursor-end');
          }}
          onMouseEnter={isReadOnly ? undefined : () => {
            // Mirror the inner div's drag-tracking handlers on the td so a
            // cursor moving through cell padding (or drifting columns during
            // autofill) still updates the active drag.
            handlers.updateCellDrag(idx, c.key);
            handlers.updateMoveTarget(idx, c.key);
            handlers.updateAutofillTarget(idx);
          }}
        >
          {/* Spec 4.1: move-drag handle on the focused idle cell. Thin strip
              at the top edge with a move cursor — mousedown starts a move-drag
              that's committed on the doc-level mouseup over a target cell. */}
          {!isReadOnly && focusedColKey === c.key && editingColKey !== c.key && (
            <div
              className="ui-sheet__movehandle"
              onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
                handlers.startMoveDrag(idx, colIdx);
              }}
              title="Drag to move cell content"
            />
          )}
          {/* Spec 5.2: autofill handle — small square at the bottom-right of
              the selection anchor. Drag down to fill subsequent rows with
              detected pattern (numeric, "Scene N", repeating, etc.). */}
          {!isReadOnly && autofillHandleColIdx === colIdx && editingColKey !== c.key && (
            <div
              className="ui-sheet__fillhandle"
              onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
                handlers.startAutofillDrag();
              }}
              title="Drag down to fill"
            />
          )}
          {editingColKey !== c.key && (
            <div
              className="scene-cell-idle scene-input ui-sheet__text"
              data-testid="scene-cell"
              data-type={c.cls}
              data-mono={c.key === 'sceneNumber' ? 'true' : undefined}
              data-row={idx}
              data-col={c.key}
              tabIndex={isReadOnly ? -1 : 0}
              onFocus={isReadOnly ? undefined : () => {
                // Spec 1.1/1.2: focus marks cell as idle-selected (active border
                // shows). Edit mode only starts on type/Shift+Enter/F2/dblclick.
                setHoveredRow(idx);
                setFocusedCell({ rowIdx: idx, colKey: c.key });
                lastFocusBodyRef.current = draftBodyRef.current;
                if ((selectedRowsSizeRef?.current || 0) > 0) {
                  setSelectedRows(new Set());
                  setSelectionAnchor(null);
                }
                if (selectAllActiveRef?.current) setSelectAllActive(false);
              }}
              onMouseDown={isReadOnly ? undefined : (e) => {
                handlers.clearTabOrigin();
                // Spec 1.5: start (or shift-extend) a multi-cell range drag.
                if (e.button === 0) handlers.startCellDrag(idx, c.key, e.shiftKey, e.ctrlKey || e.metaKey);
              }}
              onMouseEnter={isReadOnly ? undefined : () => {
                handlers.updateCellDrag(idx, c.key);
                handlers.updateMoveTarget(idx, c.key);
                handlers.updateAutofillTarget(idx);
              }}
              onDoubleClick={isReadOnly ? undefined : () => {
                handlers.enterEdit(idx, c.key, 'cursor-end');
              }}
              onKeyDown={isReadOnly ? undefined : e => {
                // Run 2 (Character column): Alt+T fills the speaker from two rows
                // above — the other side of the exchange. Intercepted before the
                // alt-key passthrough below.
                if (c.key === 'character' && e.altKey && (e.key === 't' || e.key === 'T')) {
                  e.preventDefault();
                  const src = speakerTwoAbove(draftBodyRef, idx);
                  if (src) {
                    updateScene(idx, c.key, src);
                    handlers.enterEdit(idx, c.key, 'cursor-end'); // blur-on-move saves it
                  }
                  return;
                }
                // D-124 (Longform only): Ctrl+Enter / Cmd+Enter is an EXACT alias of Enter (row-advance).
                const ctrlEnterAlias = e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && draftBodyRef.current?.format === 'Longform';
                // Let global handlers (Ctrl+Z/Ctrl+H, browser shortcuts) through.
                if (!ctrlEnterAlias && (e.ctrlKey || e.metaKey || e.altKey)) return;
                if (e.isComposing) return; // IME composition

                // Navigating to another cell by keyboard collapses any active
                // multi-cell range/multi-selection down to the destination cell
                // (Google-Sheets behavior), so a follow-up copy/delete/color acts on
                // the cell you moved TO — not the stale range. Shift+Enter / F2 open
                // edit mode (not navigation) and are excluded; Shift+Tab still counts.
                if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' ||
                    e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'Tab' ||
                    (e.key === 'Enter' && !e.shiftKey)) {
                  handlers.collapseCellSelection();
                }

                const total = draftBodyRef.current?.scenes?.length || 0;
                const colIdx = cols.findIndex(col => col.key === c.key);

                // Spec 1.1: Shift+Enter / F2 → cursor-end edit mode (no overwrite).
                if ((e.key === 'Enter' && e.shiftKey) || e.key === 'F2') {
                  e.preventDefault();
                  handlers.enterEdit(idx, c.key, 'cursor-end');
                  return;
                }
                // Run 2 (Character column): Enter in an EMPTY Character cell does
                // what Alt+T does — repeat the speaker from two rows above.
                if (e.key === 'Enter' && c.key === 'character' && !String(row[c.key] || '').trim()) {
                  e.preventDefault();
                  const src = speakerTwoAbove(draftBodyRef, idx);
                  if (src) {
                    updateScene(idx, c.key, src);
                    handlers.enterEdit(idx, c.key, 'cursor-end');
                  }
                  return;
                }
                // Spec 1.4: Enter → next row at tab-origin column (or this column).
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const nextIdx = idx + 1;
                  const tabOrigin = handlers.getTabOrigin();
                  const targetCol = tabOrigin != null ? tabOrigin : c.key;
                  handlers.clearTabOrigin();
                  if (nextIdx >= total || !focusVisible(nextIdx, targetCol, 1)) {
                    handlers.queueFocus(total, targetCol);
                    addRow();
                  }
                  return;
                }
                // Spec 1.4: Tab → next col / Shift+Tab → prev col.
                if (e.key === 'Tab') {
                  e.preventDefault();
                  if (!e.shiftKey) {
                    if (handlers.getTabOrigin() == null) handlers.setTabOrigin(c.key);
                    if (colIdx < cols.length - 1) {
                      document.querySelector(`[data-row="${idx}"][data-col="${cols[colIdx + 1].key}"]`)?.focus();
                    } else if (!(idx < total - 1 && focusVisible(idx + 1, cols[0].key, 1))) {
                      handlers.queueFocus(total, cols[0].key);
                      addRow();
                    }
                  } else {
                    handlers.clearTabOrigin();
                    if (colIdx > 0) {
                      document.querySelector(`[data-row="${idx}"][data-col="${cols[colIdx - 1].key}"]`)?.focus();
                    } else if (idx > 0) {
                      focusVisible(idx - 1, cols[cols.length - 1].key, -1);
                    }
                  }
                  return;
                }
                // Spec 1.3: arrow keys move between cells in idle mode (no edge check).
                if (e.key === 'ArrowLeft') {
                  e.preventDefault(); handlers.clearTabOrigin();
                  if (colIdx > 0) document.querySelector(`[data-row="${idx}"][data-col="${cols[colIdx - 1].key}"]`)?.focus();
                  else if (idx > 0) focusVisible(idx - 1, cols[cols.length - 1].key, -1);
                  return;
                }
                if (e.key === 'ArrowRight') {
                  e.preventDefault(); handlers.clearTabOrigin();
                  if (colIdx < cols.length - 1) document.querySelector(`[data-row="${idx}"][data-col="${cols[colIdx + 1].key}"]`)?.focus();
                  else if (idx < total - 1) focusVisible(idx + 1, cols[0].key, 1);
                  return;
                }
                if (e.key === 'ArrowUp') {
                  e.preventDefault(); handlers.clearTabOrigin();
                  if (idx > 0) focusVisible(idx - 1, c.key, -1);
                  return;
                }
                if (e.key === 'ArrowDown') {
                  e.preventDefault(); handlers.clearTabOrigin();
                  if (idx < total - 1) focusVisible(idx + 1, c.key, 1);
                  return;
                }
                // Spec 2.1: Delete OR Backspace clears the active cells.
                // - Delete: always clears, stays in idle.
                // - Backspace: clears the active grid. If it was a single cell,
                //   enter cursor edit-mode on it (Excel/Sheets convention so
                //   the user can immediately start retyping). On a multi-cell
                //   range, just clear and stay idle. (On Mac the main "delete"
                //   key IS Backspace, so this is the path most users hit.)
                if (e.key === 'Delete' || e.key === 'Backspace') {
                  e.preventDefault();
                  handlers.clearActiveCells();
                  // Backspace on a single cell additionally enters cursor mode
                  // so the user can immediately retype (Excel convention).
                  // selectedColRange is '' when there's no multi-cell range on
                  // THIS row, which means we're acting on the focused cell only.
                  if (e.key === 'Backspace' && !selectedColRange) {
                    handlers.enterEdit(idx, c.key, 'cursor-end');
                  }
                  return;
                }
                // Escape from idle → drop focus and clear any multi-cell range.
                if (e.key === 'Escape') {
                  e.preventDefault();
                  handlers.clearTabOrigin();
                  handlers.clearCellSelection();
                  e.target.blur();
                  return;
                }
                // Spec 1.1: any printable char (single-character key) → enter
                // edit with overwrite. The typed char becomes the new cell value.
                if (e.key.length === 1) {
                  e.preventDefault();
                  updateScene(idx, c.key, e.key);
                  handlers.enterEdit(idx, c.key, 'cursor-end');
                  // Run 2 (Character column): the picker opens from the FIRST typed
                  // character (registry names, shared word-prefix matcher).
                  if (c.key === 'character' && registryNames) {
                    const suggestions = matchCharacterNames(e.key, displayCharacters(registryNames));
                    setAutocomplete(suggestions.length > 0 ? { rowIdx: idx, colKey: c.key, suggestions, selIdx: 0 } : null);
                  }
                }
              }}
              style={{ fontSize: editorFontSize }}
            >
              {c.key === 'character' && registryNames && !isReadOnly ? (
                <span className="ui-sheet__name">
                  <span>{row[c.key] || ''}</span>
                  {!registryHasName(registryNames, row[c.key]) && /\p{L}/u.test(row[c.key] || '') && (
                    // Run 2 §4.3: free text is allowed but MARKED — and only an
                    // explicit click adds it to the registry (never auto-added).
                    <span className="ui-tag" data-testid="not-in-list" title="This name is not in the project's character list">
                      Not in list
                      {canEditRegistry && (
                        <button
                          type="button"
                          className="ui-sheet__iconbtn"
                          data-testid="add-to-list"
                          aria-label="Add to this project's character list"
                          title="Add to this project's character list"
                          onMouseDown={ev => ev.preventDefault()}
                          onClick={ev => { ev.stopPropagation(); handlers.addCharacterToRegistry(String(row[c.key] || '').trim()); }}
                        ><Icon icon={Plus} /></button>
                      )}
                    </span>
                  )}
                  {isHovered && (
                    <button
                      type="button"
                      className="ui-sheet__iconbtn"
                      data-testid="character-chevron"
                      aria-label="Pick from the project's character list"
                      title="Pick from the project's character list"
                      onMouseDown={ev => ev.preventDefault()}
                      onClick={ev => {
                        ev.stopPropagation();
                        handlers.enterEdit(idx, c.key, 'cursor-end');
                        const suggestions = displayCharacters(registryNames);
                        setAutocomplete(suggestions.length > 0 ? { rowIdx: idx, colKey: c.key, suggestions, selIdx: 0 } : null);
                      }}
                    ><Icon icon={ChevronDown} /></button>
                  )}
                </span>
              ) : (row[c.key] || '')}
            </div>
          )}
          {editingColKey === c.key && (
          <textarea
            className="scene-input ui-sheet__text"
            data-type={c.cls}
            data-mono={c.key === 'sceneNumber' ? 'true' : undefined}
            rows={1}
            data-row={idx}
            data-col={c.key}
            spellCheck={true}
            value={row[c.key] || ''}
            onChange={isReadOnly ? undefined : e => {
              const value = e.target.value;
              updateScene(idx, c.key, value);
              const body = draftBodyRef.current;
              const sceneCount = body?.scenes?.length || 0;
              if (c.key === 'character' && registryNames) {
                // Run 2 §4.3: the Character cell suggests from the client REGISTRY
                // (not from other rows), via the shared word-prefix matcher
                // (D-017/D-018 — same rule as the Characters column header search),
                // from the first character typed. Top option pre-highlighted so Tab
                // has something to accept.
                const suggestions = value.trim()
                  ? matchCharacterNames(value, displayCharacters(registryNames))
                  : [];
                setAutocomplete(suggestions.length > 0 ? { rowIdx: idx, colKey: c.key, suggestions, selIdx: 0 } : null);
              } else if (value.length >= 2 && sceneCount < 200) {
                // Prefix match only — a suggestion must START with what was typed.
                // This deliberately does NOT match mid-word: typing "cut" should not
                // offer "quick cutaway". Case-insensitive (both sides lowercased).
                const q = value.toLowerCase();
                const all = (body?.scenes || [])
                  .filter((_, i2) => i2 !== idx)
                  .map(r => (r[c.key] || '').trim())
                  .filter(v => v && v.toLowerCase().startsWith(q));
                const suggestions = [...new Set(all)].slice(0, 6);
                setAutocomplete(suggestions.length > 0 ? { rowIdx: idx, colKey: c.key, suggestions, selIdx: -1 } : null);
              } else {
                setAutocomplete(null);
              }
            }}
            onFocus={isReadOnly ? undefined : () => {
              setHoveredRow(idx);
              setFocusedCell({ rowIdx: idx, colKey: c.key });
              lastFocusBodyRef.current = draftBodyRef.current;
              if ((selectedRowsSizeRef?.current || 0) > 0) {
                setSelectedRows(new Set());
                setSelectionAnchor(null);
              }
              if (selectAllActiveRef?.current) setSelectAllActive(false);
            }}
            onBlur={isReadOnly ? undefined : (e) => {
              const rt = e.relatedTarget;
              // Stay in edit if focus is moving to the autocomplete dropdown.
              if (rt?.closest?.('[data-autocomplete]')) return;
              setAutocomplete(null);
              if (lastFocusBodyRef.current && lastFocusBodyRef.current !== draftBodyRef.current) {
                pushUndo(lastFocusBodyRef.current);
              }
              saveBody();
              // Pass 3: always exit edit on blur. The new cell (if any) lands in
              // idle-selected state — its onFocus sets focusedCell. If focus
              // leaves the editor entirely, clear focusedCell + hover too.
              handlers.exitEdit();
              const stillInEditor = rt?.matches?.('.scene-input, .scene-cell-idle');
              if (!stillInEditor) {
                setFocusedCell(null);
                setHoveredRow(null);
              }
            }}
            onInput={e => autoResize(e.target)}
            onMouseDown={isReadOnly ? undefined : () => { handlers.clearTabOrigin(); }}
            onKeyDown={isReadOnly ? undefined : e => {
              if ((e.ctrlKey || e.metaKey) && e.key === 'b') { e.preventDefault(); applyMarkdown('**', idx, c.key); return; }
              if ((e.ctrlKey || e.metaKey) && e.key === 'i') { e.preventDefault(); applyMarkdown('*',  idx, c.key); return; }
              if ((e.ctrlKey || e.metaKey) && e.key === 'u') { e.preventDefault(); applyMarkdown('__', idx, c.key); return; }
              const ac = autocomplete;
              if (ac && ac.rowIdx === idx && ac.colKey === c.key) {
                if (!e.shiftKey && e.key === 'ArrowDown') { e.preventDefault(); setAutocomplete(prev => prev ? { ...prev, selIdx: Math.min(prev.selIdx + 1, prev.suggestions.length - 1) } : prev); return; }
                if (!e.shiftKey && e.key === 'ArrowUp')   { e.preventDefault(); setAutocomplete(prev => prev ? { ...prev, selIdx: Math.max(prev.selIdx - 1, 0) } : prev); return; }
                // Only Enter (or clicking a choice) accepts a suggestion. Tab and
                // arrow keys must NOT auto-fill the top choice without consent — Tab
                // just closes the dropdown and moves to the next cell as normal.
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const pickIdx = ac.selIdx >= 0 ? ac.selIdx : 0;
                  updateScene(idx, c.key, ac.suggestions[pickIdx]);
                  setAutocomplete(null);
                  return;
                }
                if (e.key === 'Tab') {
                  // Run 2 (Character column ONLY): Tab ACCEPTS the highlighted
                  // option, then the fall-through below advances to the next cell —
                  // the Line column. Every other column keeps dismiss-on-Tab
                  // (DEF-075 documents this divergence).
                  if (c.key === 'character' && registryNames && !e.shiftKey && ac.suggestions.length > 0) {
                    const pickIdx = ac.selIdx >= 0 ? ac.selIdx : 0;
                    updateScene(idx, c.key, ac.suggestions[pickIdx]);
                  }
                  setAutocomplete(null); /* fall through to Tab navigation below */
                }
                if (e.key === 'Escape') { setAutocomplete(null); return; }
              }
              // Run 2 (Character column): Alt+T — or Enter in an EMPTY cell —
              // fills the speaker from two rows above (the other side of the
              // exchange). Ctrl+T is browser-reserved and deliberately unused.
              if (c.key === 'character' && (
                    (e.altKey && (e.key === 't' || e.key === 'T')) ||
                    (e.key === 'Enter' && !e.shiftKey && !String(e.target.value || '').trim())
                  )) {
                e.preventDefault();
                const src = speakerTwoAbove(draftBodyRef, idx);
                if (src) { updateScene(idx, c.key, src); setAutocomplete(null); }
                return;
              }
              const total = draftBodyRef.current?.scenes?.length || 0;
              // D-124 (Longform only): Ctrl+Enter / Cmd+Enter while editing = Enter (row-advance). Other
              // formats keep their pre-existing behaviour for the chord (a plain newline is never inserted
              // by this editor, so the chord falls through to the same branch there too).
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                setAutocomplete(null);
                const nextIdx = idx + 1;
                // Spec 1.4: if Tab origin is recorded, Enter returns to that column on next row.
                const tabOrigin = handlers.getTabOrigin();
                const targetCol = tabOrigin != null ? tabOrigin : c.key;
                handlers.clearTabOrigin();
                if (nextIdx >= total || !focusVisible(nextIdx, targetCol, 1)) {
                  handlers.queueFocus(total, targetCol);
                  addRow();
                }
                return;
              }
              if (e.key === 'Tab') {
                e.preventDefault();
                setAutocomplete(null);
                const colIdx = cols.findIndex(col => col.key === c.key);
                if (!e.shiftKey) {
                  // Spec 1.4: record where tabbing started so Enter can return here.
                  if (handlers.getTabOrigin() == null) handlers.setTabOrigin(c.key);
                  if (colIdx < cols.length - 1) {
                    document.querySelector(`[data-row="${idx}"][data-col="${cols[colIdx + 1].key}"]`)?.focus();
                  } else if (!(idx < total - 1 && focusVisible(idx + 1, cols[0].key, 1))) {
                    handlers.queueFocus(total, cols[0].key);
                    addRow();
                  }
                } else {
                  // Shift+Tab: backward navigation cancels the forward tab-origin.
                  handlers.clearTabOrigin();
                  if (colIdx > 0) {
                    document.querySelector(`[data-row="${idx}"][data-col="${cols[colIdx - 1].key}"]`)?.focus();
                  } else if (idx > 0) {
                    focusVisible(idx - 1, cols[cols.length - 1].key, -1);
                  }
                }
                return;
              }
              // Spec 1.1: Escape reverts the cell to its pre-edit value and
              // returns to idle-selected (focus the cell's now-mounted div).
              if (e.key === 'Escape') {
                e.preventDefault();
                setAutocomplete(null);
                handlers.clearTabOrigin();
                handlers.revertEdit();
                // After revertEdit, editingCell becomes null; the cell remounts as
                // <div>. Defer focus to next tick so React commits the swap first.
                setTimeout(() => {
                  const el = document.querySelector(`[data-row="${idx}"][data-col="${c.key}"]`);
                  if (el) el.focus();
                }, 0);
                return;
              }
              if (!e.shiftKey && e.key === 'ArrowLeft') {
                if (e.target.selectionStart === 0 && e.target.selectionEnd === 0) {
                  e.preventDefault();
                  handlers.clearTabOrigin();
                  const colIdx = cols.findIndex(col => col.key === c.key);
                  if (colIdx > 0) {
                    const el = document.querySelector(`[data-row="${idx}"][data-col="${cols[colIdx - 1].key}"]`);
                    if (el) el.focus();
                  } else if (idx > 0) {
                    const el = document.querySelector(`[data-row="${idx - 1}"][data-col="${cols[cols.length - 1].key}"]`);
                    if (el) el.focus();
                  }
                }
              }
              if (!e.shiftKey && e.key === 'ArrowRight') {
                if (e.target.selectionStart === e.target.value.length && e.target.selectionEnd === e.target.value.length) {
                  e.preventDefault();
                  handlers.clearTabOrigin();
                  const colIdx = cols.findIndex(col => col.key === c.key);
                  if (colIdx < cols.length - 1) {
                    const el = document.querySelector(`[data-row="${idx}"][data-col="${cols[colIdx + 1].key}"]`);
                    if (el) el.focus();
                  } else if (idx < total - 1) {
                    const el = document.querySelector(`[data-row="${idx + 1}"][data-col="${cols[0].key}"]`);
                    if (el) el.focus();
                  }
                }
              }
              // ArrowUp/ArrowDown (no autocomplete open) intentionally do nothing
              // here: once you're editing a cell, up/down move the cursor within the
              // cell's own text and never jump to the next/previous cell. Leaving the
              // cell is Enter, Tab, or clicking out. (Autocomplete navigation is
              // handled above; Shift+Arrow still moves the whole row.)
              if (e.shiftKey && e.key === 'ArrowUp')   { e.preventDefault(); setAutocomplete(null); moveRow(idx, -1, c.key); }
              if (e.shiftKey && e.key === 'ArrowDown') { e.preventDefault(); setAutocomplete(null); moveRow(idx,  1, c.key); }
            }}
            placeholder={isReadOnly ? '' : sentenceLabel(c.label)}
            readOnly={isReadOnly}
            style={{ fontSize: editorFontSize }}
          />
          )}
          {row._links?.[c.key] && (
            <div className="ui-sheet__link">
              {isReadOnly ? (
                <a href={row._links[c.key].url} target="_blank" rel="noopener noreferrer">
                  {row._links[c.key].text || row._links[c.key].url}
                </a>
              ) : (
                <>
                  <a href={row._links[c.key].url} target="_blank" rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}>
                    {row._links[c.key].text || row._links[c.key].url}
                  </a>
                  <button type="button" className="ui-sheet__iconbtn" aria-label="Remove link" title="Remove link"
                    onMouseDown={e => { e.preventDefault(); e.stopPropagation(); removeLink(idx, c.key); }}><Icon icon={X} /></button>
                </>
              )}
            </div>
          )}
          {!row._links?.[c.key] && (() => {
            const detectedUrl = extractCellUrl(row[c.key]);
            return detectedUrl ? (
              <div className="ui-sheet__link">
                <a href={detectedUrl} target="_blank" rel="noreferrer"
                  onClick={e => e.stopPropagation()}>
                  Open link
                </a>
              </div>
            ) : null;
          })()}
          {!isReadOnly && (
            <>
              <span className="ui-sheet__cellbtns">
                <button
                  type="button"
                  className="ui-sheet__swatchbtn"
                  data-tint={tintOf(row._cellColors?.[c.key])}
                  data-show={(isHovered || row._cellColors?.[c.key]) ? 'true' : 'false'}
                  aria-label="Cell color"
                  title="Cell color"
                  onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setColorPickerCell(colorPickerCellKey === c.key ? null : { rowIdx: idx, colKey: c.key }); setColorPickerRow(null); }}
                  onClick={e => e.stopPropagation()}
                />
                {LINK_COLS[format]?.includes(c.key) && (
                  <button
                    type="button"
                    className="ui-sheet__iconbtn"
                    data-show={(isHovered || row._links?.[c.key]) ? 'true' : 'false'}
                    aria-pressed={row._links?.[c.key] ? 'true' : undefined}
                    aria-label="Insert link"
                    title="Insert link"
                    onMouseDown={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      const selText = window.getSelection()?.toString() || '';
                      setLinkDraft({ text: row._links?.[c.key]?.text || selText, url: row._links?.[c.key]?.url || '' });
                      setLinkPopover(linkPopoverColKey === c.key ? null : { rowIdx: idx, colKey: c.key });
                      setColorPickerCell(null); setColorPickerRow(null);
                    }}
                    onClick={e => e.stopPropagation()}
                  ><Icon icon={Link2} /></button>
                )}
              </span>
              {colorPickerCellKey === c.key && (
                <div className="ui-sheet__pop ui-sheet__pop--cell" onClick={e => e.stopPropagation()}>
                  <ColorSwatch onSelect={color => updateCellColor(idx, c.key, color)} />
                </div>
              )}
              {LINK_COLS[format]?.includes(c.key) && linkPopoverColKey === c.key && linkDraft && (
                <div className={`ui-sheet__pop ${idx === 0 ? 'ui-sheet__pop--below' : 'ui-sheet__pop--above'} ui-stack`} onClick={e => e.stopPropagation()}>
                  <h3 className="ui-heading">Insert link</h3>
                  <Input
                    aria-label="Display text"
                    autoFocus
                    placeholder="Display text"
                    value={linkDraft.text}
                    onChange={e => setLinkDraft(prev => ({ ...prev, text: e.target.value }))}
                  />
                  <Input
                    aria-label="URL"
                    placeholder="https://"
                    value={linkDraft.url}
                    onChange={e => setLinkDraft(prev => ({ ...prev, url: e.target.value }))}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault(); saveLink(idx, c.key, linkDraft.text, linkDraft.url); }
                      if (e.key === 'Escape') setLinkPopover(null);
                    }}
                  />
                  <div className="ui-cluster ui-cluster--end">
                    <Button variant="quiet" onClick={() => setLinkPopover(null)}>Cancel</Button>
                    <Button variant="primary" disabled={!linkDraft.url.trim()} onClick={() => saveLink(idx, c.key, linkDraft.text, linkDraft.url)}>Save link</Button>
                  </div>
                </div>
              )}
              {autocomplete && autocomplete.rowIdx === idx && autocomplete.colKey === c.key && autocomplete.suggestions.length > 0 && (
                <ul data-autocomplete className="ui-suggest" role="listbox" aria-label={`${sentenceLabel(c.label)} suggestions`}>
                  {autocomplete.suggestions.map((s, i) => (
                    <li
                      key={i}
                      role="option"
                      aria-selected={autocomplete.selIdx === i}
                      className="ui-suggest__item"
                      onMouseDown={e => { e.preventDefault(); updateScene(idx, c.key, s); setAutocomplete(null); }}
                      onMouseEnter={() => setAutocomplete(prev => prev ? { ...prev, selIdx: i } : prev)}
                    >
                      {s}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </td>
      ))}
      {showQualNotesCol && (
        <td className="scene-td ui-sheet__cell">
          <textarea
            className="scene-input ui-sheet__text"
            data-type="notes"
            rows={1}
            data-row={idx}
            data-col="qualNotes"
            spellCheck={true}
            value={row.qualNotes || ''}
            onChange={canEditQualNotes ? e => { updateScene(idx, 'qualNotes', e.target.value); } : undefined}
            onFocus={canEditQualNotes ? () => { setHoveredRow(idx); setFocusedCell({ rowIdx: idx, colKey: 'qualNotes' }); lastFocusBodyRef.current = draftBodyRef.current; } : undefined}
            onBlur={canEditQualNotes ? e => { setFocusedCell(null); if (lastFocusBodyRef.current && lastFocusBodyRef.current !== draftBodyRef.current) pushUndo(lastFocusBodyRef.current); saveBody(); if (!e.relatedTarget?.classList.contains('scene-input')) setHoveredRow(null); } : undefined}
            onInput={e => autoResize(e.target)}
            placeholder={canEditQualNotes ? 'Note' : ''}
            readOnly={!canEditQualNotes}
            style={{ fontSize: editorFontSize }}
          />
        </td>
      )}
    </tr>
  );
});

// ── Color swatch picker ─────────────────────────────────────────
// The role tints a row or cell can carry (ROW_COLORS minus the retired ones), then Clear. onSelect gets the stored value.
export function ColorSwatch({ onSelect }) {
  return (
    <div className="ui-tints" role="group" aria-label="Color">
      {ROW_COLORS.filter(c => !c.retired).map(c => (
        <button
          key={c.label}
          type="button"
          className="ui-tint"
          data-tint={c.tint}
          aria-label={c.label}
          title={c.label}
          onClick={e => { e.stopPropagation(); onSelect(c.value); }}
        >
          {!c.value && <Icon icon={X} />}
        </button>
      ))}
    </div>
  );
}

export default SceneRow;
